import classNames from 'classnames';
import { useState, useEffect, useRef, MutableRefObject } from 'react';

import useOutsideClick from '~hooks/useOutsideClick';
import CloseIcon from '~images/inline/close.inline.svg';

type Props = {
    content: string;
    dataQa: string;
    guid: string;
    isOpen: boolean;
    onClick: () => void;
    parentRef: MutableRefObject<HTMLElement>;
    positionClasses?: string;
};

const Dialog = ({
    content,
    dataQa,
    guid,
    isOpen,
    onClick,
    parentRef,
    positionClasses = 'md:top-[-4.5rem]',
}: Props) => {
    const [isDialogFocused, setIsDialogFocused] = useState(false);
    const closeBtn = useRef(null as HTMLButtonElement | null);
    const dialog = useRef(null as HTMLDivElement | null);

    useOutsideClick(dialog as MutableRefObject<HTMLDivElement>, onClick);

    useEffect(() => {
        if (isOpen) {
            setIsDialogFocused(true);
            closeBtn.current?.focus();
        } else if (isDialogFocused) {
            setIsDialogFocused(false);
            parentRef.current?.focus();
        }
    }, [isOpen]);

    const afterClasses =
        'after:border-[5px] after:border-transparent after:bottom-full after:content-none after:left-[74px] after:absolute after:top-auto after:rotate-180 after:border-t-gray-dark';
    const afterClassesMd =
        'md:after:top-full md:after:left-[74px] md:after:rotate-0 md:after:bottom-full';

    return (
        <div
            className={classNames(
                'dialog absolute p-3 w-72 rounded text-sm text-white bg-gray-dark items-start z-50',
                isOpen ? 'flex' : 'hidden',
                positionClasses,
                afterClasses,
                afterClassesMd
            )}
            role="dialog"
            aria-labelledby={`dialog-label-${guid}`}
            data-qa={`${dataQa}-dialog`}
            ref={dialog}
        >
            <p className="text-left" id={`dialog-label-${guid}`}>
                {content}
            </p>
            <button
                className="h-4 w-4"
                aria-label="Close"
                type="button"
                ref={closeBtn}
                onClick={onClick}
                data-qa={`${dataQa}-close-tooltip`}
            >
                <CloseIcon />
            </button>
        </div>
    );
};

export default Dialog;
